'use client';

import Link from 'next/link';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import { APP_TAGLINE, COMPANY_NAME } from '@/config/version';
import { AxisLogo, AxisWordmark } from '@/components/ui/CoreOpsLogo';

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return <div className="w-7 h-7" />;

  return (
    <button
      type="button"
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      className="w-7 h-7 rounded-md flex items-center justify-center opacity-40 hover:opacity-70 transition-opacity"
      aria-label="Toggle theme"
    >
      {theme === 'dark' ? (
        <Sun className="w-3.5 h-3.5 text-secondary-400" />
      ) : (
        <Moon className="w-3.5 h-3.5 text-gray-400" />
      )}
    </button>
  );
}

export default function AuthLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="relative min-h-screen bg-white dark:bg-secondary-950 overflow-hidden">
      {/* ═══ Blueprint grid — full width, fades at angle from left to right ═══ */}
      <div
        className="hidden lg:block absolute inset-0 z-0 pointer-events-none"
        style={{
          maskImage: 'linear-gradient(105deg, rgba(0,0,0,1) 25%, rgba(0,0,0,0.5) 45%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0) 75%)',
          WebkitMaskImage: 'linear-gradient(105deg, rgba(0,0,0,1) 25%, rgba(0,0,0,0.5) 45%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0) 75%)',
        }}
      >
        {/* Major grid */}
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(59,130,246,0.6) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59,130,246,0.6) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            animation: 'grid-drift 30s linear infinite',
          }}
        />
        {/* Minor grid */}
        <div
          className="absolute inset-0 opacity-[0.09]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)
            `,
            backgroundSize: '15px 15px',
            animation: 'grid-drift 45s linear infinite reverse',
          }}
        />

        {/* Floor plan wireframes */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.15]" xmlns="http://www.w3.org/2000/svg">
          <rect x="5%" y="12%" width="18%" height="18%" rx="2" fill="none" stroke="rgba(59,130,246,0.8)" strokeWidth="1" strokeDasharray="4 4">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="8s" repeatCount="indefinite" />
          </rect>
          <rect x="26%" y="18%" width="12%" height="25%" rx="2" fill="none" stroke="rgba(59,130,246,0.6)" strokeWidth="1">
            <animate attributeName="opacity" values="0.3;0.8;0.3" dur="10s" repeatCount="indefinite" />
          </rect>
          <rect x="42%" y="8%" width="22%" height="12%" rx="2" fill="none" stroke="rgba(59,130,246,0.5)" strokeWidth="1" strokeDasharray="8 4">
            <animate attributeName="opacity" values="0.4;0.9;0.4" dur="12s" repeatCount="indefinite" />
          </rect>
          <rect x="68%" y="15%" width="14%" height="20%" rx="2" fill="none" stroke="rgba(59,130,246,0.4)" strokeWidth="1">
            <animate attributeName="opacity" values="0.3;0.7;0.3" dur="11s" repeatCount="indefinite" />
          </rect>

          {/* Corridors */}
          <line x1="23%" y1="21%" x2="26%" y2="21%" stroke="rgba(59,130,246,0.6)" strokeWidth="1">
            <animate attributeName="opacity" values="0.3;0.7;0.3" dur="6s" repeatCount="indefinite" />
          </line>
          <line x1="38%" y1="30%" x2="42%" y2="30%" stroke="rgba(59,130,246,0.5)" strokeWidth="1">
            <animate attributeName="opacity" values="0.2;0.6;0.2" dur="7s" repeatCount="indefinite" />
          </line>
          <line x1="64%" y1="20%" x2="68%" y2="20%" stroke="rgba(59,130,246,0.4)" strokeWidth="1">
            <animate attributeName="opacity" values="0.3;0.6;0.3" dur="8s" repeatCount="indefinite" />
          </line>

          {/* Lower rooms */}
          <rect x="8%" y="52%" width="16%" height="22%" rx="2" fill="none" stroke="rgba(59,130,246,0.5)" strokeWidth="1">
            <animate attributeName="opacity" values="0.4;0.8;0.4" dur="9s" repeatCount="indefinite" />
          </rect>
          <rect x="28%" y="58%" width="20%" height="18%" rx="2" fill="none" stroke="rgba(59,130,246,0.7)" strokeWidth="1" strokeDasharray="6 3">
            <animate attributeName="opacity" values="0.3;0.7;0.3" dur="11s" repeatCount="indefinite" />
          </rect>
          <rect x="52%" y="50%" width="15%" height="26%" rx="2" fill="none" stroke="rgba(59,130,246,0.4)" strokeWidth="1">
            <animate attributeName="opacity" values="0.5;0.9;0.5" dur="8s" repeatCount="indefinite" />
          </rect>
          <rect x="72%" y="55%" width="18%" height="15%" rx="2" fill="none" stroke="rgba(59,130,246,0.3)" strokeWidth="1" strokeDasharray="4 4">
            <animate attributeName="opacity" values="0.2;0.6;0.2" dur="10s" repeatCount="indefinite" />
          </rect>

          {/* Connection nodes */}
          <circle cx="23%" cy="21%" r="3" fill="rgba(59,130,246,0.4)">
            <animate attributeName="r" values="2;4;2" dur="4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0.8;0.3" dur="4s" repeatCount="indefinite" />
          </circle>
          <circle cx="38%" cy="43%" r="3" fill="rgba(59,130,246,0.3)">
            <animate attributeName="r" values="2;4;2" dur="5s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.2;0.7;0.2" dur="5s" repeatCount="indefinite" />
          </circle>
          <circle cx="52%" cy="50%" r="3" fill="rgba(59,130,246,0.5)">
            <animate attributeName="r" values="2;3;2" dur="6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.4;0.9;0.4" dur="6s" repeatCount="indefinite" />
          </circle>
          <circle cx="72%" cy="55%" r="3" fill="rgba(59,130,246,0.3)">
            <animate attributeName="r" values="2;4;2" dur="7s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0.6;0.3" dur="7s" repeatCount="indefinite" />
          </circle>

          {/* Dimension lines */}
          <line x1="5%" y1="38%" x2="24%" y2="38%" stroke="rgba(59,130,246,0.3)" strokeWidth="0.5" strokeDasharray="2 3">
            <animate attributeName="opacity" values="0.2;0.5;0.2" dur="8s" repeatCount="indefinite" />
          </line>
          <line x1="42%" y1="42%" x2="67%" y2="42%" stroke="rgba(59,130,246,0.3)" strokeWidth="0.5" strokeDasharray="2 3">
            <animate attributeName="opacity" values="0.3;0.6;0.3" dur="9s" repeatCount="indefinite" />
          </line>
          <line x1="8%" y1="82%" x2="48%" y2="82%" stroke="rgba(59,130,246,0.2)" strokeWidth="0.5" strokeDasharray="2 3">
            <animate attributeName="opacity" values="0.2;0.4;0.2" dur="10s" repeatCount="indefinite" />
          </line>
        </svg>

        {/* Radial glow — centered on left half */}
        <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-primary-500/[0.06] to-transparent rounded-full blur-3xl" />
      </div>

      {/* ═══ Layout: two columns ═══ */}
      <div className="relative z-10 flex min-h-screen">
        {/* Left side — Brand */}
        <div className="hidden w-1/2 lg:flex lg:flex-col lg:items-center lg:justify-center relative">
          {/* Content — centered brand */}
          <div className="flex flex-col items-center text-center">
            <Link href="/" className="flex items-center gap-6 mb-8">
              <AxisLogo size="2xl" />
              <AxisWordmark size="2xl" />
            </Link>
            <p className="text-3xl font-brand italic text-secondary-300 tracking-wide leading-relaxed">
              {APP_TAGLINE}
            </p>
          </div>

          {/* Bottom credentials */}
          <div className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-2">
            <span className="text-xs text-secondary-600 tracking-wider uppercase">Veteran-Owned &middot; SDVOSB</span>
            <span className="text-xs text-secondary-700">
              &copy; {new Date().getFullYear()} {COMPANY_NAME}
            </span>
          </div>
        </div>

        {/* Right side — Auth forms (theme-aware) */}
        <div className="relative flex w-full flex-col justify-center px-4 sm:px-6 lg:w-1/2 lg:px-12 bg-white dark:bg-secondary-950">
          {/* Theme toggle */}
          <div className="absolute top-6 right-6 z-10">
            <ThemeToggle />
          </div>

          {/* Mobile logo */}
          <div className="mb-8 lg:hidden">
            <Link href="/" className="flex items-center gap-3">
              <AxisLogo size="sm" />
              <div className="flex flex-col">
                <AxisWordmark size="sm" />
                <span className="text-xs text-primary-600 dark:text-primary-400/70 mt-1">{APP_TAGLINE}</span>
              </div>
            </Link>
          </div>

          <div className="mx-auto w-full max-w-md">
            {children}
          </div>
        </div>
      </div>

      {/* Keyframes */}
      <style jsx>{`
        @keyframes grid-drift {
          0% { transform: translate(0, 0); }
          100% { transform: translate(60px, 60px); }
        }
      `}</style>
    </div>
  );
}
